import styled, { useTheme } from 'styled-components';
import {
  breakpoint,
  ContentCard,
  Heading,
  MegaphoneIcon,
  CartIcon,
  TagIcon,
  EnvelopeIcon,
  SecondaryButton,
  SvgComponentProps,
} from '@grays-npm/component-library';

import { data as defaultCardContent } from '../../mock-data/whats-next.mock';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../state/modal';
import { DashboardContactUs, DashboardPromoteItem } from '../../components';

export type WhatsNextItem = {
  icon: 'megaphone' | 'envelope' | 'tag' | 'cart';
  title: string;
  body: string;
  actionContent: React.ReactNode;
  actionLink: string;
};

const iconMap = {
  tag: (props: SvgComponentProps) => (
    <TagIcon width={40} height={40} {...props} />
  ),
  megaphone: (props: SvgComponentProps) => (
    <MegaphoneIcon width={30} height={40} {...props} />
  ),
  envelope: (props: SvgComponentProps) => (
    <EnvelopeIcon width={40} height={40} {...props} />
  ),
  cart: (props: SvgComponentProps) => (
    <CartIcon width={48} height={40} {...props} />
  ),
} as const;

const WhatsNextContainer = styled.section``;

const WhatsNextHeading = styled(Heading)`
  margin: ${({ theme: { spacings } }) =>
    `${spacings[5].rem} 0 ${spacings[4].rem}`};

  ${breakpoint('md')`
    margin: ${({ theme: { spacings } }) =>
      `${spacings[6].rem} 0 ${spacings[5].rem}`};
  `}
`;

const CardsContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;

  > * {
    flex: 1 2 45%;

    margin: ${({ theme }) => `0 0 ${theme.spacings[3].rem} 0`};

    &:nth-child(2n) {
      margin: ${({ theme }) =>
        `0 0 ${theme.spacings[4].rem} ${theme.spacings[3].rem}`};
    }

    ${breakpoint('lg')`
      flex: 1 2 20%;

      margin-bottom: 0px;

      &:not(:first-child){
        margin: ${({ theme }) => `0 0 0 ${theme.spacings[3].rem}`};
      }
    `};
  }
`;

export type WhatsNextProps = {
  cardContents?: WhatsNextItem[];
};

export const WhatsNext: React.FC<WhatsNextProps> = ({
  cardContents = defaultCardContent,
}) => {
  let navigate = useNavigate();
  const { setModalProps } = useModal();
  const theme = useTheme();

  const actionClick = (linkName: string) => {
    if (linkName === 'contactmodal') {
      setModalProps({
        isOpen: true,
        onClose: () => {},
        children: <DashboardContactUs />,
      });
    } else if (linkName === 'promotemodal') {
      setModalProps({
        isOpen: true,
        onClose: () => {},
        children: <DashboardPromoteItem />,
      });
    } else {
      navigate(linkName);
    }
  };

  return (
    <>
      <WhatsNextHeading size={4}>What's next?</WhatsNextHeading>
      <WhatsNextContainer>
        <CardsContainer>
          {cardContents.map(
            ({ icon, actionContent, title, body, actionLink }) => {
              const Icon = iconMap[icon];
              return (
                <ContentCard
                  key={icon}
                  icon={<Icon fill={theme.colors.system.systemBlue} />}
                  title={title}
                  body={body}
                  size={{ xs: 'small', lg: 'large' }}
                >
                  <SecondaryButton
                    size="small"
                    fullWidth={true}
                    onClick={() => actionClick(actionLink)}
                  >
                    {actionContent}
                  </SecondaryButton>
                </ContentCard>
              );
            }
          )}
        </CardsContainer>
      </WhatsNextContainer>
    </>
  );
};
